import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { termsContent } from '../data/termsContent';
import { privacyContent } from '../data/privacyContent';
import './Signup.css';

interface ChallengeData {
  sessionId: string;
  challenge: string;
  position: number;
  repeatCount: number;
}

const Signup: React.FC = () => {
  const navigate = useNavigate();
  const { isAuthenticated, login } = useAuth();
  const [step, setStep] = useState<'form' | 'challenge'>('form');
  const [formData, setFormData] = useState({
    companyName: '',
    email: '',
    password: '',
    passwordConfirm: '',
    bizNo: '',
    acceptTerms: false,
    acceptPrivacy: false
  });
  const [challengeData, setChallengeData] = useState<ChallengeData | null>(null);
  const [challengeResponse, setChallengeResponse] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState(0);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [showPrivacyModal, setShowPrivacyModal] = useState(false);

  // 이미 로그인된 상태라면 홈으로 리다이렉트
  useEffect(() => {
    if (isAuthenticated) {
      navigate('/', { replace: true });
    }
  }, [isAuthenticated, navigate]);

  // 비밀번호 강도 계산
  useEffect(() => {
    const { password } = formData;
    let strength = 0;

    if (password.length >= 8) strength++;
    if (password.length >= 12) strength++;
    if (/[a-z]/.test(password)) strength++;
    if (/[A-Z]/.test(password)) strength++;
    if (/[0-9]/.test(password)) strength++;
    if (/[^a-zA-Z0-9]/.test(password)) strength++;

    setPasswordStrength(Math.min(strength, 4));
  }, [formData.password]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  const validateForm = (): boolean => {
    if (!formData.companyName.trim()) {
      setError('회사명을 입력해주세요.');
      return false;
    }

    if (!formData.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      setError('올바른 이메일 주소를 입력해주세요.');
      return false;
    }

    if (formData.password.length < 8) {
      setError('비밀번호는 최소 8자 이상이어야 합니다.');
      return false;
    }

    if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])/.test(formData.password)) {
      setError('비밀번호는 대문자, 소문자, 숫자를 포함해야 합니다.');
      return false;
    }

    if (formData.password !== formData.passwordConfirm) {
      setError('비밀번호가 일치하지 않습니다.');
      return false;
    }

    if (!formData.acceptTerms) {
      setError('이용약관에 동의해주세요.');
      return false;
    }

    if (!formData.acceptPrivacy) {
      setError('개인정보처리방침에 동의해주세요.');
      return false;
    }

    return true;
  };

  const handleGenerateChallenge = async () => {
    if (!formData.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      setError('올바른 이메일 주소를 먼저 입력해주세요.');
      return;
    }

    // 이용약관 동의 체크
    if (!formData.acceptTerms) {
      setError('이용약관에 동의해주세요.');
      return;
    }

    if (!formData.acceptPrivacy) {
      setError('개인정보처리방침에 동의해주세요.');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await axios.post('/api/auth/challenge', {
        email: formData.email
      });

      setChallengeData(response.data);
      setStep('challenge');
      setChallengeResponse('');
    } catch (err: any) {
      setError(err.response?.data?.message || '챌린지 생성 중 오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (step === 'form') {
      if (!validateForm()) return;
      await handleGenerateChallenge();
    } else {
      await handleSignup();
    }
  };

  const handleSignup = async () => {
    if (!challengeData) {
      setError('챌린지 정보가 없습니다. 다시 시도해주세요.');
      return;
    }

    if (!challengeResponse.trim()) {
      setError('챌린지 응답을 입력해주세요.');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await axios.post('/api/auth/signup', {
        email: formData.email,
        password: formData.password,
        companyName: formData.companyName,
        bizNo: formData.bizNo || null,
        acceptTerms: formData.acceptTerms,
        acceptPrivacy: formData.acceptPrivacy,
        challengeResponse: challengeResponse,
        challengeSessionId: challengeData.sessionId
      });

      // 회원가입 성공 시 자동 로그인
      if (response.data.token && response.data.user) {
        localStorage.setItem('token', response.data.token);
        localStorage.setItem('user', JSON.stringify(response.data.user));

        // AuthContext의 login 함수로 상태 업데이트
        await login(formData.email, formData.password);

        // 홈으로 이동
        navigate('/', { replace: true });
      }
    } catch (err: any) {
      setError(err.response?.data?.message || '회원가입 처리 중 오류가 발생했습니다.');

      // 챌린지 실패 시 다시 폼으로
      if (err.response?.data?.message?.includes('챌린지')) {
        setStep('form');
        setChallengeData(null);
        setChallengeResponse('');
      }
    } finally {
      setLoading(false);
    }
  };

  const getPasswordStrengthText = () => {
    switch (passwordStrength) {
      case 0:
      case 1: return '약함';
      case 2: return '보통';
      case 3: return '강함';
      case 4: return '매우 강함';
      default: return '';
    }
  };

  const getPasswordStrengthColor = () => {
    switch (passwordStrength) {
      case 0:
      case 1: return '#ef4444';
      case 2: return '#f59e0b';
      case 3: return '#10b981';
      case 4: return '#059669';
      default: return '#d1d5db';
    }
  };

  const isFormValid =
    formData.companyName.trim() !== '' &&
    formData.email.trim() !== '' &&
    formData.password.length >= 8 &&
    formData.password === formData.passwordConfirm &&
    formData.acceptTerms &&
    formData.acceptPrivacy;

  return (
    <div className="signup-page">
      <div className="signup-container">
        <div className="signup-card fade-in">
          <div className="signup-header">
            <h1 className="signup-title">회원가입</h1>
            <p className="signup-subtitle">
              {step === 'form'
                ? 'EasyAutoCall과 함께 시작하세요'
                : '이메일 확인'}
            </p>
          </div>

          <form className="signup-form" onSubmit={handleSubmit}>
            {error && (
              <div className="error-message">
                {error}
              </div>
            )}

            {step === 'form' ? (
              <>
                <div className="form-group">
                  <label className="form-label" htmlFor="companyName">
                    회사명 <span className="required">*</span>
                  </label>
                  <input
                    id="companyName"
                    name="companyName"
                    type="text"
                    className="form-input"
                    placeholder="회사명을 입력하세요"
                    value={formData.companyName}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div className="form-group">
                  <label className="form-label" htmlFor="bizNo">
                    사업자등록번호 (선택)
                  </label>
                  <input
                    id="bizNo"
                    name="bizNo"
                    type="text"
                    className="form-input"
                    placeholder="000-00-00000"
                    value={formData.bizNo}
                    onChange={handleChange}
                  />
                </div>

                <div className="form-group">
                  <label className="form-label" htmlFor="email">
                    이메일 <span className="required">*</span>
                  </label>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    className="form-input"
                    placeholder="your@email.com"
                    value={formData.email}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div className="form-group">
                  <label className="form-label" htmlFor="password">
                    비밀번호 <span className="required">*</span>
                  </label>
                  <input
                    id="password"
                    name="password"
                    type="password"
                    className="form-input"
                    placeholder="최소 8자 (대문자, 소문자, 숫자 포함)"
                    value={formData.password}
                    onChange={handleChange}
                    required
                  />
                  {formData.password && (
                    <div className="password-strength">
                      <div className="strength-bar">
                        <div
                          className="strength-fill"
                          style={{
                            width: `${(passwordStrength / 4) * 100}%`,
                            backgroundColor: getPasswordStrengthColor()
                          }}
                        />
                      </div>
                      <span
                        className="strength-text"
                        style={{ color: getPasswordStrengthColor() }}
                      >
                        {getPasswordStrengthText()}
                      </span>
                    </div>
                  )}
                </div>

                <div className="form-group">
                  <label className="form-label" htmlFor="passwordConfirm">
                    비밀번호 확인 <span className="required">*</span>
                  </label>
                  <input
                    id="passwordConfirm"
                    name="passwordConfirm"
                    type="password"
                    className="form-input"
                    placeholder="비밀번호를 다시 입력하세요"
                    value={formData.passwordConfirm}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div className="agreements">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      name="acceptTerms"
                      checked={formData.acceptTerms}
                      onChange={handleChange}
                      required
                    />
                    <span>
                      <button
                        type="button"
                        className="terms-link"
                        onClick={() => setShowTermsModal(true)}
                      >
                        이용약관
                      </button>에 동의합니다 <span className="required">*</span>
                    </span>
                  </label>

                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      name="acceptPrivacy"
                      checked={formData.acceptPrivacy}
                      onChange={handleChange}
                      required
                    />
                    <span>
                      <button
                        type="button"
                        className="terms-link"
                        onClick={() => setShowPrivacyModal(true)}
                      >
                        개인정보처리방침
                      </button>에 동의합니다 <span className="required">*</span>
                    </span>
                  </label>
                </div>

                <button
                  type="submit"
                  className="btn btn-primary btn-lg"
                  style={{ width: '100%' }}
                  disabled={!isFormValid || loading}
                >
                  {loading ? '처리 중...' : '다음'}
                </button>
              </>
            ) : (
              <>
                <div className="challenge-section">
                  <div className="challenge-info">
                    <p className="challenge-email">
                      <strong>{formData.email}</strong>
                    </p>
                    <p className="challenge-instruction">
                      {challengeData?.challenge}
                    </p>
                  </div>

                  <div className="form-group">
                    <label className="form-label" htmlFor="challengeResponse">
                      답변
                    </label>
                    <input
                      id="challengeResponse"
                      type="text"
                      className="form-input"
                      placeholder="문자를 입력하세요"
                      value={challengeResponse}
                      onChange={(e) => setChallengeResponse(e.target.value)}
                      autoFocus
                      required
                    />
                    <p className="challenge-hint">
                      예: 3번째 문자가 'a'이고 2번 입력이라면 "aa"를 입력
                    </p>
                  </div>
                </div>

                <div className="button-group">
                  <button
                    type="button"
                    className="btn btn-secondary btn-lg"
                    onClick={() => {
                      setStep('form');
                      setChallengeData(null);
                      setChallengeResponse('');
                      setError('');
                    }}
                    disabled={loading}
                  >
                    이전
                  </button>
                  <button
                    type="submit"
                    className="btn btn-primary btn-lg"
                    disabled={!challengeResponse.trim() || loading}
                  >
                    {loading ? '처리 중...' : '가입하기'}
                  </button>
                </div>
              </>
            )}
          </form>

          <div className="signup-footer">
            <p className="login-text">
              이미 계정이 있으신가요?{' '}
              <Link to="/login" className="login-link">
                로그인
              </Link>
            </p>
          </div>
        </div>

        <div className="signup-info slide-in-right">
          <h2 className="info-title">
            간편한<br />
            회원가입
          </h2>
          <ul className="info-list">
            <li>
              <span className="info-icon">✓</span>
              <span>빠른 가입 절차</span>
            </li>
            <li>
              <span className="info-icon">✓</span>
              <span>이메일 인증만으로 완료</span>
            </li>
            <li>
              <span className="info-icon">✓</span>
              <span>즉시 서비스 이용 가능</span>
            </li>
            <li>
              <span className="info-icon">✓</span>
              <span>안전한 정보 보호</span>
            </li>
          </ul>
        </div>
      </div>

      {/* 이용약관 모달 */}
      {showTermsModal && (
        <div className="modal-overlay" onClick={() => setShowTermsModal(false)}>
          <div className="modal-content terms-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>이용약관</h2>
              <button className="modal-close" onClick={() => setShowTermsModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <div className="terms-content">
                {termsContent.terms.map((term, index) => (
                  <div key={index} className="term-section">
                    <h3>{term.title}</h3>
                    <p style={{ whiteSpace: 'pre-line' }}>{term.content}</p>
                  </div>
                ))}
                <div className="company-info">
                  <h3>회사 정보</h3>
                  <p>회사명: {termsContent.companyName}</p>
                  <p>대표자: {termsContent.companyInfo.ceo}</p>
                  <p>사업자등록번호: {termsContent.companyInfo.businessNumber}</p>
                  <p>본사: {termsContent.companyInfo.headquarters}</p>
                  <p>연구소: {termsContent.companyInfo.researchLab}</p>
                  <p>전화: {termsContent.companyInfo.tel}</p>
                  <p>이메일: {termsContent.companyInfo.email}</p>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button
                className="btn btn-secondary"
                onClick={() => setShowTermsModal(false)}
              >
                취소
              </button>
              <button
                className="btn btn-primary"
                onClick={() => {
                  setFormData({ ...formData, acceptTerms: true });
                  setShowTermsModal(false);
                }}
              >
                동의
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 개인정보처리방침 모달 */}
      {showPrivacyModal && (
        <div className="modal-overlay" onClick={() => setShowPrivacyModal(false)}>
          <div className="modal-content terms-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>개인정보처리방침</h2>
              <button className="modal-close" onClick={() => setShowPrivacyModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <div className="terms-content">
                <p className="effective-date">시행일자: {privacyContent.effectiveDate}</p>
                {privacyContent.sections.map((section, index) => (
                  <div key={index} className="term-section">
                    <h3>{section.title}</h3>
                    <p style={{ whiteSpace: 'pre-line' }}>{section.content}</p>
                  </div>
                ))}
                <div className="company-info">
                  <h3>개인정보보호책임자</h3>
                  <p>이름: {privacyContent.companyInfo.privacyOfficer.name}</p>
                  <p>부서: {privacyContent.companyInfo.privacyOfficer.department}</p>
                  <p>전화: {privacyContent.companyInfo.privacyOfficer.tel}</p>
                  <p>이메일: {privacyContent.companyInfo.privacyOfficer.email}</p>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button
                className="btn btn-secondary"
                onClick={() => setShowPrivacyModal(false)}
              >
                취소
              </button>
              <button
                className="btn btn-primary"
                onClick={() => {
                  setFormData({ ...formData, acceptPrivacy: true });
                  setShowPrivacyModal(false);
                }}
              >
                동의
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Signup;
